/**
 * Community — the owner-review form page and its reward screen (design 5b + 5c,
 * `pages/community/review/new.tsx`): hrefs, URL-input validation, the live preview built from
 * the band's draft, the "When you publish" panel and the reward screen's lines. Pure, no fetch,
 * no React; unit-tested in `tests/community-review.spec.ts`.
 *
 * Like `model.ts`, nothing here holds a points value, a tier threshold or a founding rule:
 * the points come from the summary's table, the standing from `/me` and the publish answer.
 * One exception, said where it lives: the Review Trio's three (`REVIEW_TRIO_COUNT`).
 */
import type { OwnerAnswers, OwnerQuestionSetDef } from 'lib/dealhunt/types'
import {
  buyAgainChip,
  NEW_REVIEW_PATH,
  newReviewHref,
  readChips,
  spotsLeft,
  threadHref,
  tierLabel,
  type ReadChip,
} from './model'
import type {
  CommunityFeedItem,
  CommunityPoints,
  CommunityPublishStanding,
  CommunityStanding,
  CommunityTier,
} from './types'

// The phase-1c fields live in `types.ts` now; these names are kept for the components that
// were written against them while the two branches were apart.
export type CommunityPointsTable = CommunityPoints
export type CommunityStandingNext = CommunityStanding
export type CommunityPublishStandingNext = CommunityPublishStanding

// ── routes ────────────────────────────────────────────────────────────────────

// `newReviewHref` and `threadHref` live in `model.ts` (one path literal each); re-exported so
// the form page's imports read as one module.
export { newReviewHref, threadHref }
export const REVIEW_NEW_PATH = NEW_REVIEW_PATH

/**
 * `?product=` as the form will resolve it, or null when it cannot be a product slug. Checked
 * BEFORE any fetch: a made-up value must cost DealHunt nothing (a 404 without a resolve).
 */
export function reviewProductParam(raw: unknown): string | null {
  return typeof raw === 'string' && /^[a-z0-9-]{1,120}$/.test(raw) ? raw : null
}

// ── the band's draft and its preview ──────────────────────────────────────────

/** What `OwnerNoteBand` reports through `onDraftChange`: the answers chosen so far, the
 *  sentence as typed, and the public photos uploaded so far. `buy_again` is null until
 *  answered. */
export interface OwnerNoteDraft {
  answers: Omit<OwnerAnswers, 'buy_again'> & { buy_again: boolean | null }
  note: string
  /** At least one public photo is uploaded (a tile still uploading, or failed, is not). */
  hasPhoto: boolean
  photoCount: number
  /** The first uploaded photo's picture (a local preview or DealHunt's URL); null without one. */
  cover: string | null
}

/** What a host may hand the band to start from (`OwnerNoteBand` `seed`): a restored draft —
 *  the answers and the sentence, never photos (the band keeps its own photo slot,
 *  `photoDraftKey`). */
export type OwnerNoteSeed = Pick<OwnerNoteDraft, 'answers' | 'note'>

export const EMPTY_DRAFT: OwnerNoteDraft = {
  answers: {
    ownership_length: '',
    still_using: '',
    use_cases: [],
    reads: {},
    gave_out: null,
    hours_per_charge: null,
    buy_again: null,
  },
  note: '',
  hasPhoto: false,
  photoCount: 0,
  cover: null,
}

/** The preview row (design 5b, "How it appears in the feed"): what a feed row would show. */
export interface DraftPreview {
  /** The ownership answer's label, for the Owner pill; null until answered. */
  ownership: string | null
  /** The sentence, trimmed; null while empty. */
  sentence: string | null
  /** Departure chips — the feed's own rule (`readChips`). */
  chips: ReadChip[]
  /** null until the buy-again question is answered. */
  again: { text: string; yes: boolean } | null
}

/**
 * The draft as a feed row would print it, with labels from the question set the band loaded
 * (no second fetch). Built through a `CommunityFeedItem` so the chips follow `readChips` /
 * `buyAgainChip` — the preview cannot drift from the feed. A read whose id the set does not
 * name prints no chip, like the feed.
 */
export function draftPreview(
  draft: OwnerNoteDraft,
  questions: OwnerQuestionSetDef | null
): DraftPreview {
  const label = (options: OwnerQuestionSetDef['reads'] | undefined, id: string): string =>
    options?.find(o => o.id === id)?.label ?? ''
  const reads: CommunityFeedItem['labels']['reads'] = []
  Object.entries(draft.answers.reads).forEach(([id, value]) => {
    // A skipped read (null) prints nothing, like the feed.
    if (value) {
      reads.push({ id, label: label(questions?.reads, id), value })
    }
  })
  // Only the two fields `readChips` and `buyAgainChip` read; a draft has no id, author or date.
  const item = {
    note: { answers: { ...draft.answers, buy_again: draft.answers.buy_again === true } },
    labels: { reads },
  } as unknown as CommunityFeedItem
  const sentence = draft.note.trim()
  return {
    ownership: label(questions?.ownership_length, draft.answers.ownership_length) || null,
    sentence: sentence || null,
    chips: readChips(item),
    again: draft.answers.buy_again === null ? null : buyAgainChip(item),
  }
}

// ── "When you publish" ────────────────────────────────────────────────────────

export interface PublishPanelInput {
  productName: string
  /** The summary's table; null when the summary could not be read — no points are named. */
  points: CommunityPointsTable | null
  /** The summary's founding counter; null with the summary. */
  founding: { count: number; cap: number } | null
  /** Published owner reviews on this product (the resolve's owners enrichment). */
  publishedCount: number
  /** `/me`'s standing; null without a device or against an older DealHunt. */
  standing: CommunityStandingNext | null
  /** A session, or a claimed device: the review will publish under an account. */
  signedIn: boolean
  /** false = an edit of a review that is already live: it earns no review points. */
  counts: boolean
  /** The draft carries at least one uploaded public photo (it earns the photo bonus at publish). */
  hasPhoto: boolean
}

export interface PublishPanelRow {
  id: 'review' | 'photo' | 'founding' | 'rank'
  label: string
  value: string
  detail: string | null
}

export interface PublishPanel {
  rows: PublishPanelRow[]
  /** Signed out: what signing in adds. */
  signIn: string | null
  /** The product-page line, or null for an edit (it adds nobody). */
  owners: string | null
  /** Shown for an edit instead of the rows. */
  edit: string | null
  /** The daily points cap is reached: shown instead of the points rows. */
  capped: string | null
}

/** The points a new review here earns: the review, plus the first-owner bonus while the
 *  product has no published review. null without the points table. */
export function reviewAward(
  points: CommunityPointsTable | null,
  publishedCount: number
): { award: number; bonus: number } | null {
  if (!points) {
    return null
  }
  const bonus = publishedCount === 0 ? points.first_review_bonus ?? 0 : 0
  return { award: points.review + bonus, bonus }
}

export function ownersStatLine(publishedCount: number): string {
  let where = 'where nobody has answered yet'
  if (publishedCount === 1) {
    where = 'where 1 owner has answered so far'
  } else if (publishedCount > 1) {
    where = `where ${publishedCount.toLocaleString('en-US')} owners have answered so far`
  }
  return `It also adds 1 to the owner stats on the product page, ${where}.`
}

/**
 * The daily points cap, said before the reader writes and again on the reward screen. The
 * three is DealHunt's rule (`/me` sends what is left, not the cap); it is in this sentence only
 * because the product owner's wording names it — keep it equal to DealHunt's cap.
 */
/** The cap is DealHunt's (`points.reviews_per_day`); 3 is only what to say when an older
 *  DealHunt does not send it. */
export const DAILY_CAP_DEFAULT = 3
export function dailyCapLine(perDay: number | undefined = DAILY_CAP_DEFAULT): string {
  return `You’ve earned points for ${perDay} owner reviews today. This one still publishes and counts for the product’s owner stats; points pick up again tomorrow.`
}
/** @deprecated use `dailyCapLine(points.reviews_per_day)` */
export const DAILY_CAP_LINE = dailyCapLine()

export const EDIT_COUNTS_LINE =
  'Your review already counts toward your points. An edit is checked again before it replaces the live one.'

/**
 * The community sign-in gate (`signInGateFor('community')`, lib/owner-notes/signin-gate.ts) for a
 * new review: Publish is the sign-in, so the copy says when it happens instead of offering it as
 * an extra. The composer passes it only after `panelReady` (client-only), never into server HTML.
 * No gated line sells speed or names a moderator (aca4da79), and none offers anonymity.
 */
export interface GatedCopy {
  gated?: boolean
}

/**
 * The panel beside the form (design 5b). Every figure is DealHunt's: the award from the points
 * table, the founding number from `founding_next`, the next tier from `next_tier` — SoundGuys
 * only compares `points_to_go` with the award it is about to name.
 */
export function publishPanel(input: PublishPanelInput, opts: GatedCopy = {}): PublishPanel {
  const { standing } = input
  if (!input.counts) {
    return { rows: [], signIn: null, owners: null, edit: EDIT_COUNTS_LINE, capped: null }
  }
  const owners = ownersStatLine(input.publishedCount)
  const earn = reviewAward(input.points, input.publishedCount)
  if (!input.signedIn) {
    return {
      rows: [],
      signIn: signInLine(earn?.award ?? null, input.founding, opts),
      owners,
      edit: null,
      capped: null,
    }
  }
  // The daily cap: the review still publishes and still counts for the owner stats and the
  // founding number — only the points rows give way, to a notice that is not an error.
  const capped = standing?.reviews_today_left === 0
  const rows: PublishPanelRow[] = []
  if (earn && !capped) {
    rows.push({
      id: 'review',
      label: earn.bonus > 0 ? 'Owner review, first on this product' : 'Owner review',
      value: `+${earn.award}`,
      detail:
        earn.bonus > 0
          ? `+${input.points?.review} for the review and +${earn.bonus} because nobody has reviewed the ${input.productName} yet.`
          : null,
    })
  }
  // The photo bonus is paid AT PUBLISH when the review carries ≥ 1 public photo
  // (contract-review-photos §4) — no moderator step to wait for, so no fine print.
  const photoPoints = input.points?.photo_verified
  if (input.hasPhoto && !capped && typeof photoPoints === 'number' && photoPoints > 0) {
    rows.push({ id: 'photo', label: 'Photo', value: `+${photoPoints}`, detail: null })
  }
  const next = standing?.founding_next
  if (typeof next === 'number') {
    rows.push({
      id: 'founding',
      label: 'Early Owner badge',
      value: input.founding ? `#${next} of ${input.founding.cap}` : `#${next}`,
      detail: null,
    })
  }
  const tier = standing?.next_tier
  if (earn && !capped && standing && tier && tier.points_to_go <= earn.award) {
    rows.push({
      id: 'rank',
      label: 'Rank',
      value: `${tierLabel(standing.tier)} → ${tierLabel(tier.id)}`,
      detail: null,
    })
  }
  return {
    rows,
    signIn: null,
    owners,
    edit: null,
    capped: capped ? dailyCapLine(input.points?.reviews_per_day) : null,
  }
}

/**
 * The phone's one line ABOVE the form (the full panel stays below it, after the preview): what
 * publishing earns, and for a signed-out reader the sign-in that earns it. Null = nothing worth
 * a line above the form (an edit that earns nothing, the daily cap, no points table signed in).
 */
export function phonePublishLine(
  input: PublishPanelInput,
  { gated = false }: GatedCopy = {}
): { text: string; signIn: boolean } | null {
  if (!input.counts) {
    return null
  }
  const earn = reviewAward(input.points, input.publishedCount)
  if (!input.signedIn && gated) {
    return {
      text: earn
        ? `Publishing needs a sign-in — now, or at Publish. It earns +${earn.award} points.`
        : 'Publishing needs a sign-in — now, or at Publish.',
      signIn: true,
    }
  }
  if (!input.signedIn) {
    return {
      text: earn
        ? `When you publish: sign in first to earn +${earn.award} points.`
        : 'When you publish: sign in first to earn points.',
      signIn: true,
    }
  }
  if (!earn || input.standing?.reviews_today_left === 0) {
    return null
  }
  return { text: `When you publish: +${earn.award} points.`, signIn: false }
}

/** Signed out: what signing in before publishing adds — gated, what it earns once signed in at
 *  Publish (the sign-in is no longer optional, so it is not sold as one). */
export function signInLine(
  award: number | null,
  founding: { count: number; cap: number } | null,
  { gated = false }: GatedCopy = {}
): string {
  const left = founding ? spotsLeft(founding) : 0
  const gains = [
    award === null ? null : `+${award} points`,
    'a place on the leaderboard',
    left > 0 && founding
      ? `the Early Owner badge (${left} of the first ${founding.cap} spots ${
          left === 1 ? 'is' : 'are'
        } left)`
      : null,
  ].filter((g): g is string => g !== null)
  const list =
    gains.length > 1 ? `${gains.slice(0, -1).join(', ')} and ${gains[gains.length - 1]}` : gains[0]
  if (gated) {
    return `Sign in now or at Publish. Your review then earns ${list}, and can carry photos.`
  }
  return `Sign in before you publish to add photos, and earn ${list}.`
}

// ── the reward screen (design 5c) ─────────────────────────────────────────────

/** The Review Trio badge is named for its count. SoundGuys holds this one number because the
 *  only place DealHunt states it — the member page's badge detail — lags a publish by the cache
 *  window, which is exactly when this screen shows. */
export const REVIEW_TRIO_COUNT = 3

/** "This month" for a linked author whose public profile is off. */
export const REWARD_PROFILE_OFF = 'Your public profile is off, so you are not ranked'
/** "This month" for an anonymous author: signing in is what ranks the review (no figure — the
 *  points are the tile beside it, DealHunt's). */
export const REWARD_SIGN_IN_TO_RANK = 'Sign in and this review goes on the leaderboard'

export interface RewardTile {
  id: 'points' | 'rank' | 'month'
  label: string
  value: string
  detail: string
}

export interface RewardView {
  /** The hexagon: the founding number this publish took, or the tier badge. */
  badge: { kind: 'founding'; number: number } | { kind: 'tier'; tier: CommunityTier }
  title: string
  tiles: RewardTile[]
}

/**
 * The reward screen from the publish answer (`earned`), `/me` before the publish (`before`, for
 * "Was <tier>") and `/me` re-read after it (`after`, for this month's rank — the publish answer
 * carries none). Nothing is computed but "points before" = points − points awarded, both
 * figures from the same answer.
 */
export function rewardView(
  earned: CommunityPublishStandingNext,
  before: CommunityStanding | null,
  after: CommunityStanding | null,
  /** The summary's points table, for the cap's wording; null = default wording. */
  pointsTable: CommunityPointsTable | null = null,
  /** The review is linked to an account (a claim, or `/me`'s `profile.claimed`). false = an
   *  anonymous author: they are not ranked because they have not signed in, not because a
   *  profile is off. */
  linked = true
): RewardView {
  const { tier } = earned
  const badge: RewardView['badge'] =
    earned.founding_new && earned.founding_number !== null
      ? { kind: 'founding', number: earned.founding_number }
      : { kind: 'tier', tier }

  let title = 'Your owner review is live.'
  if (badge.kind === 'founding') {
    title = `You’re Early Owner #${badge.number}.`
  } else if (earned.tier_reached) {
    const reached = tierLabel(earned.tier_reached)
    title = `You’re now ${/^[AEIOU]/.test(reached) ? 'an' : 'a'} ${reached}.`
  }

  const was = Math.max(0, earned.points - earned.points_awarded)
  // DealHunt's own sentence wins when it sends one (the ledger owns the wording).
  const points: RewardTile = earned.capped
    ? {
        id: 'points',
        label: 'Points',
        value: '+0 today',
        detail: earned.points_note || dailyCapLine(pointsTable?.reviews_per_day),
      }
    : {
        id: 'points',
        label: 'Points',
        value: `+${earned.points_awarded.toLocaleString('en-US')}`,
        detail: `${was.toLocaleString('en-US')} → ${earned.points.toLocaleString('en-US')} pts`,
      }

  // "Was" only when DealHunt says the tier moved and the earlier tier is known: from `/me`
  // before the publish, or Listener when the reader had no points at all.
  let previous: string | null = null
  if (earned.tier_reached) {
    if (before) {
      previous = tierLabel(before.tier)
    } else if (was === 0) {
      previous = tierLabel('listener')
    }
  }
  const nextTier = after?.next_tier
  let rankDetail = 'The top tier points can reach'
  if (previous) {
    rankDetail = `Was ${previous}`
  } else if (nextTier) {
    rankDetail = `${nextTier.points_to_go.toLocaleString('en-US')} pts to ${tierLabel(nextTier.id)}`
  } else if (!after) {
    rankDetail = ''
  }
  const rank: RewardTile = { id: 'rank', label: 'Rank', value: tierLabel(tier), detail: rankDetail }

  const month: RewardTile = { id: 'month', label: 'This month', value: '—', detail: '' }
  // `/me` after the publish wins over the publish answer once it says member (a sign-in on the
  // reward screen made the author one after the answer was sent).
  const member = earned.member || Boolean(after?.member)
  if (!member) {
    month.detail = linked ? REWARD_PROFILE_OFF : REWARD_SIGN_IN_TO_RANK
  } else if (typeof after?.rank_month === 'number') {
    month.value = `#${after.rank_month}`
    month.detail = 'On the leaderboard'
  } else if (after) {
    month.detail = 'Not ranked yet'
  }
  return { badge, title, tiles: [points, rank, month] }
}

/** "Your review of the X is live…" — the thread sentence is said only when there is a thread
 *  to link (a product slug). */
export function rewardLead(productName: string, hasThread: boolean): string {
  const live = `Your review of the ${productName} is live in the community and on the product page.`
  return hasThread ? `${live} It’s also a thread now, so people can ask you about it.` : live
}

/**
 * "Keep going": the Review Trio clause while the badge is still ahead and the reader is a
 * member (badges are members'); otherwise just the invitation. `published` = the reader's
 * published reviews from `/me` after the publish; null while unknown.
 */
export function keepGoingLine(published: number | null, member: boolean): string {
  const left = published === null ? 0 : REVIEW_TRIO_COUNT - published
  if (member && left > 0) {
    return `Own something else? ${left} more owner ${
      left === 1 ? 'review unlocks' : 'reviews unlock'
    } the Review Trio badge.`
  }
  return 'Own something else? Leave an owner review on it too.'
}

/**
 * The reward screen's line under the tiles (contract-review-photos §4): DealHunt's own points
 * sentence when it sends one, verbatim (Constraint 1006e9c1); else "Photo +15" when this publish
 * paid the photo bonus — `photo_bonus` from DealHunt, or, from a DealHunt older than that flag,
 * public photos attached (`attached`). Never at the daily cap (the points tile says it, and
 * `points_awarded` is 0), and the figure is the points table's: without it, no number. The bonus
 * is already inside `points_awarded` — this line only names it.
 */
export function rewardPointsLine(
  earned: CommunityPublishStandingNext,
  attached: boolean,
  points: CommunityPointsTable | null
): string | null {
  if (earned.capped) {
    return null
  }
  if (earned.points_note) {
    return earned.points_note
  }
  const paid = typeof earned.photo_bonus === 'boolean' ? earned.photo_bonus : attached
  const bonus = points?.photo_verified
  if (!paid || typeof bonus !== 'number' || bonus <= 0) {
    return null
  }
  return `Photo +${bonus.toLocaleString('en-US')} — counted in your points above.`
}
